// ─── YatraNest Flight Demo Data ──────────────────────────────────────────────
// All flights are DEMO FARES. No real booking is possible without a live provider.
// is_demo = true on every record. Never create real bookings from this data.

export interface Airport {
  iata: string;
  name: string;
  city: string;
  country: string;
  timezone: string; // IANA timezone
  terminal?: string;
}

export interface FlightSegment {
  id: string;
  flightNumber: string;
  airline: string;
  airlineCode: string;
  airlineLogo: string;
  departureAirport: Airport;
  arrivalAirport: Airport;
  departureISO: string; // ISO 8601 with offset
  arrivalISO: string;   // ISO 8601 with offset
  durationMinutes: number;
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first';
  stops: StopInfo[];
  aircraft?: string;
}

export interface StopInfo {
  airport: Airport;
  layoverMinutes: number;
}

export interface BaggageInfo {
  checkedKg: number;
  cabinKg: number;
  checkedPieces?: number;
}

export interface FareInfo {
  baseFare: number;
  taxes: number;
  serviceFee: number;
  total: number;
  currency: string;
  refundable: boolean;
  fareRules: string;
  cancellationFee: number;
  changeFee: number;
}

export interface FlightOffer {
  id: string;
  is_demo: true;
  demoLabel: string;
  segments: FlightSegment[];
  baggage: BaggageInfo;
  fare: FareInfo;
  seatsAvailable?: number;
  validatingCarrier: string;
  bookingClass: string;
  lastTicketingDate?: string;
}

// ─── Airport Database ─────────────────────────────────────────────────────────
export const AIRPORTS: Record<string, Airport> = {
  KTM: { iata: 'KTM', name: 'Tribhuvan International Airport', city: 'Kathmandu', country: 'Nepal', timezone: 'Asia/Kathmandu' },
  PKR: { iata: 'PKR', name: 'Pokhara International Airport', city: 'Pokhara', country: 'Nepal', timezone: 'Asia/Kathmandu' },
  BHR: { iata: 'BHR', name: 'Bharatpur Airport', city: 'Bharatpur', country: 'Nepal', timezone: 'Asia/Kathmandu' },
  DEL: { iata: 'DEL', name: 'Indira Gandhi International Airport', city: 'Delhi', country: 'India', timezone: 'Asia/Kolkata' },
  BOM: { iata: 'BOM', name: 'Chhatrapati Shivaji Maharaj International Airport', city: 'Mumbai', country: 'India', timezone: 'Asia/Kolkata' },
  BLR: { iata: 'BLR', name: 'Kempegowda International Airport', city: 'Bengaluru', country: 'India', timezone: 'Asia/Kolkata' },
  DXB: { iata: 'DXB', name: 'Dubai International Airport', city: 'Dubai', country: 'UAE', timezone: 'Asia/Dubai' },
  DOH: { iata: 'DOH', name: 'Hamad International Airport', city: 'Doha', country: 'Qatar', timezone: 'Asia/Qatar' },
  BKK: { iata: 'BKK', name: 'Suvarnabhumi Airport', city: 'Bangkok', country: 'Thailand', timezone: 'Asia/Bangkok' },
  SIN: { iata: 'SIN', name: 'Singapore Changi Airport', city: 'Singapore', country: 'Singapore', timezone: 'Asia/Singapore' },
  KUL: { iata: 'KUL', name: 'Kuala Lumpur International Airport', city: 'Kuala Lumpur', country: 'Malaysia', timezone: 'Asia/Kuala_Lumpur' },
  LHR: { iata: 'LHR', name: 'Heathrow Airport', city: 'London', country: 'UK', timezone: 'Europe/London' },
  CDG: { iata: 'CDG', name: 'Charles de Gaulle Airport', city: 'Paris', country: 'France', timezone: 'Europe/Paris' },
  FRA: { iata: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'Germany', timezone: 'Europe/Berlin' },
  JFK: { iata: 'JFK', name: 'John F. Kennedy International Airport', city: 'New York', country: 'USA', timezone: 'America/New_York' },
  NRT: { iata: 'NRT', name: 'Narita International Airport', city: 'Tokyo', country: 'Japan', timezone: 'Asia/Tokyo' },
  ICN: { iata: 'ICN', name: 'Incheon International Airport', city: 'Seoul', country: 'South Korea', timezone: 'Asia/Seoul' },
  PEK: { iata: 'PEK', name: 'Beijing Capital International Airport', city: 'Beijing', country: 'China', timezone: 'Asia/Shanghai' },
  HKG: { iata: 'HKG', name: 'Hong Kong International Airport', city: 'Hong Kong', country: 'China', timezone: 'Asia/Hong_Kong' },
  SYD: { iata: 'SYD', name: 'Sydney Kingsford Smith Airport', city: 'Sydney', country: 'Australia', timezone: 'Australia/Sydney' },
};

// ─── Airline Logos (placeholder SVG data URIs) ────────────────────────────────
export const AIRLINE_LOGOS: Record<string, string> = {
  '6E': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/IndiGo_Airlines_logo.svg/200px-IndiGo_Airlines_logo.svg.png',
  'AI': 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/Air_India_Logo.svg/200px-Air_India_Logo.svg.png',
  'RA': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Nepal_Airlines_logo.svg/200px-Nepal_Airlines_logo.svg.png',
  'EK': 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/Emirates_logo.svg/200px-Emirates_logo.svg.png',
  'QR': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/Qatar_Airways_Logo.svg/200px-Qatar_Airways_Logo.svg.png',
  'TG': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Thai_Airways_Logo.svg/200px-Thai_Airways_Logo.svg.png',
  'SQ': 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Singapore_Airlines_Logo_2.svg/200px-Singapore_Airlines_Logo_2.svg.png',
  'MH': 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Malaysia_Airlines_Logo.svg/200px-Malaysia_Airlines_Logo.svg.png',
  'BA': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/42/British_Airways_Logo.svg/200px-British_Airways_Logo.svg.png',
  'LH': 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b8/Lufthansa_Logo_2018.svg/200px-Lufthansa_Logo_2018.svg.png',
};

// ─── Helper: build ISO string for a given date + time in a timezone ───────────
// We use fixed offsets since we cannot use Intl.DateTimeFormat in server-safe code
const TZ_OFFSETS: Record<string, string> = {
  'Asia/Kathmandu': '+05:45',
  'Asia/Kolkata': '+05:30',
  'Asia/Dubai': '+04:00',
  'Asia/Qatar': '+03:00',
  'Asia/Bangkok': '+07:00',
  'Asia/Singapore': '+08:00',
  'Asia/Kuala_Lumpur': '+08:00',
  'Asia/Tokyo': '+09:00',
  'Asia/Seoul': '+09:00',
  'Asia/Shanghai': '+08:00',
  'Asia/Hong_Kong': '+08:00',
  'Europe/London': '+01:00',
  'Europe/Paris': '+02:00',
  'Europe/Berlin': '+02:00',
  'America/New_York': '-04:00',
  'Australia/Sydney': '+10:00',
};

export function buildISO(date: string, time: string, timezone: string): string {
  const offset = TZ_OFFSETS[timezone] || '+00:00';
  return `${date}T${time}:00${offset}`;
}

export function addMinutes(isoString: string, minutes: number): string {
  const ms = new Date(isoString).getTime() + minutes * 60 * 1000;
  const d = new Date(ms);
  // Return as UTC ISO
  return d.toISOString();
}

// ─── Demo Flight Offers ───────────────────────────────────────────────────────
// Base date: 2026-08-04 (today per context)
const BASE_DATE = '2026-08-04';
const RETURN_DATE = '2026-08-11';

export const DEMO_FLIGHT_OFFERS: FlightOffer[] = [
  // ── KTM → DEL (IndiGo, Direct) ──
  {
    id: 'demo-flight-ktm-del-6e-001',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: '6E',
    bookingClass: 'Q',
    seatsAvailable: 7,
    segments: [{
      id: 'seg-001',
      flightNumber: '6E 1234',
      airline: 'IndiGo',
      airlineCode: '6E',
      airlineLogo: AIRLINE_LOGOS['6E'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.DEL,
      departureISO: buildISO(BASE_DATE, '08:30', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '10:00', 'Asia/Kolkata'),
      durationMinutes: 105,
      cabinClass: 'economy',
      stops: [],
      aircraft: 'Airbus A320',
    }],
    baggage: { checkedKg: 15, cabinKg: 7, checkedPieces: 1 },
    fare: { baseFare: 9800, taxes: 2100, serviceFee: 600, total: 12500, currency: 'NPR', refundable: false, fareRules: 'Non-refundable. Changes allowed with fee.', cancellationFee: 3500, changeFee: 2000 },
  },
  // ── KTM → DEL (Air India, Direct) ──
  {
    id: 'demo-flight-ktm-del-ai-002',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'AI',
    bookingClass: 'M',
    seatsAvailable: 12,
    segments: [{
      id: 'seg-002',
      flightNumber: 'AI 233',
      airline: 'Air India',
      airlineCode: 'AI',
      airlineLogo: AIRLINE_LOGOS['AI'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.DEL,
      departureISO: buildISO(BASE_DATE, '14:15', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '15:45', 'Asia/Kolkata'),
      durationMinutes: 105,
      cabinClass: 'economy',
      stops: [],
      aircraft: 'Boeing 737',
    }],
    baggage: { checkedKg: 23, cabinKg: 8, checkedPieces: 1 },
    fare: { baseFare: 11200, taxes: 2400, serviceFee: 600, total: 14200, currency: 'NPR', refundable: true, fareRules: 'Refundable with cancellation fee. Changes allowed.', cancellationFee: 2000, changeFee: 1500 },
  },
  // ── KTM → DEL (Nepal Airlines, Direct) ──
  {
    id: 'demo-flight-ktm-del-ra-003',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'RA',
    bookingClass: 'Y',
    seatsAvailable: 4,
    segments: [{
      id: 'seg-003',
      flightNumber: 'RA 202',
      airline: 'Nepal Airlines',
      airlineCode: 'RA',
      airlineLogo: AIRLINE_LOGOS['RA'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.DEL,
      departureISO: buildISO(BASE_DATE, '07:00', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '08:30', 'Asia/Kolkata'),
      durationMinutes: 105,
      cabinClass: 'economy',
      stops: [],
      aircraft: 'Boeing 757',
    }],
    baggage: { checkedKg: 20, cabinKg: 7 },
    fare: { baseFare: 10500, taxes: 2200, serviceFee: 600, total: 13300, currency: 'NPR', refundable: false, fareRules: 'Non-refundable. No changes permitted.', cancellationFee: 5000, changeFee: 0 },
  },
  // ── KTM → DXB (Emirates, Direct) ──
  {
    id: 'demo-flight-ktm-dxb-ek-004',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'EK',
    bookingClass: 'V',
    seatsAvailable: 9,
    segments: [{
      id: 'seg-004',
      flightNumber: 'EK 640',
      airline: 'Emirates',
      airlineCode: 'EK',
      airlineLogo: AIRLINE_LOGOS['EK'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.DXB,
      departureISO: buildISO(BASE_DATE, '23:30', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '02:15', 'Asia/Dubai'),
      durationMinutes: 270,
      cabinClass: 'economy',
      stops: [],
      aircraft: 'Boeing 777',
    }],
    baggage: { checkedKg: 30, cabinKg: 7, checkedPieces: 2 },
    fare: { baseFare: 28000, taxes: 5500, serviceFee: 800, total: 34300, currency: 'NPR', refundable: true, fareRules: 'Refundable. Changes allowed with fee.', cancellationFee: 3000, changeFee: 2500 },
  },
  // ── KTM → BKK (Thai Airways, 1 stop BOM) ──
  {
    id: 'demo-flight-ktm-bkk-tg-005',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'TG',
    bookingClass: 'T',
    seatsAvailable: 15,
    segments: [{
      id: 'seg-005',
      flightNumber: 'TG 316',
      airline: 'Thai Airways',
      airlineCode: 'TG',
      airlineLogo: AIRLINE_LOGOS['TG'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.BKK,
      departureISO: buildISO(BASE_DATE, '10:00', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '19:45', 'Asia/Bangkok'),
      durationMinutes: 465,
      cabinClass: 'economy',
      stops: [{ airport: AIRPORTS.BOM, layoverMinutes: 120 }],
      aircraft: 'Airbus A330',
    }],
    baggage: { checkedKg: 30, cabinKg: 7, checkedPieces: 2 },
    fare: { baseFare: 22000, taxes: 4800, serviceFee: 800, total: 27600, currency: 'NPR', refundable: false, fareRules: 'Non-refundable. Changes allowed with fee.', cancellationFee: 4000, changeFee: 2000 },
  },
  // ── KTM → SIN (Singapore Airlines, Direct) ──
  {
    id: 'demo-flight-ktm-sin-sq-006',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'SQ',
    bookingClass: 'Y',
    seatsAvailable: 6,
    segments: [{
      id: 'seg-006',
      flightNumber: 'SQ 422',
      airline: 'Singapore Airlines',
      airlineCode: 'SQ',
      airlineLogo: AIRLINE_LOGOS['SQ'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.SIN,
      departureISO: buildISO(BASE_DATE, '16:30', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '23:00', 'Asia/Singapore'),
      durationMinutes: 330,
      cabinClass: 'economy',
      stops: [],
      aircraft: 'Boeing 787',
    }],
    baggage: { checkedKg: 30, cabinKg: 7, checkedPieces: 2 },
    fare: { baseFare: 32000, taxes: 6500, serviceFee: 800, total: 39300, currency: 'NPR', refundable: true, fareRules: 'Refundable with fee. Changes allowed.', cancellationFee: 3500, changeFee: 2000 },
  },
  // ── KTM → LHR (Qatar Airways, 1 stop DOH) ──
  {
    id: 'demo-flight-ktm-lhr-qr-007',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'QR',
    bookingClass: 'Q',
    seatsAvailable: 3,
    segments: [{
      id: 'seg-007',
      flightNumber: 'QR 641',
      airline: 'Qatar Airways',
      airlineCode: 'QR',
      airlineLogo: AIRLINE_LOGOS['QR'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.LHR,
      departureISO: buildISO(BASE_DATE, '01:30', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '13:45', 'Europe/London'),
      durationMinutes: 870,
      cabinClass: 'economy',
      stops: [{ airport: AIRPORTS.DOH, layoverMinutes: 180 }],
      aircraft: 'Boeing 777',
    }],
    baggage: { checkedKg: 30, cabinKg: 7, checkedPieces: 2 },
    fare: { baseFare: 78000, taxes: 15000, serviceFee: 1200, total: 94200, currency: 'NPR', refundable: true, fareRules: 'Refundable. Changes allowed with fee.', cancellationFee: 5000, changeFee: 3000 },
  },
  // ── KTM → DEL Business (Air India) ──
  {
    id: 'demo-flight-ktm-del-ai-biz-008',
    is_demo: true,
    demoLabel: 'Demo fare — live booking unavailable',
    validatingCarrier: 'AI',
    bookingClass: 'C',
    seatsAvailable: 2,
    segments: [{
      id: 'seg-008',
      flightNumber: 'AI 233',
      airline: 'Air India',
      airlineCode: 'AI',
      airlineLogo: AIRLINE_LOGOS['AI'],
      departureAirport: AIRPORTS.KTM,
      arrivalAirport: AIRPORTS.DEL,
      departureISO: buildISO(BASE_DATE, '14:15', 'Asia/Kathmandu'),
      arrivalISO: buildISO(BASE_DATE, '15:45', 'Asia/Kolkata'),
      durationMinutes: 105,
      cabinClass: 'business',
      stops: [],
      aircraft: 'Boeing 737',
    }],
    baggage: { checkedKg: 35, cabinKg: 10, checkedPieces: 2 },
    fare: { baseFare: 42000, taxes: 8500, serviceFee: 1000, total: 51500, currency: 'NPR', refundable: true, fareRules: 'Fully refundable. Changes allowed.', cancellationFee: 0, changeFee: 0 },
  },
];

// ─── Provider Adapter Interface ───────────────────────────────────────────────
// Implement this interface when connecting a real flight provider (e.g. Amadeus, Sabre, Travelport)
export interface FlightProviderAdapter {
  searchFlights(params: FlightSearchParams): Promise<FlightOffer[]>;
  getFlightOffer(offerId: string): Promise<FlightOffer | null>;
  revalidateFlightOffer(offerId: string): Promise<{ valid: boolean; newPrice?: number; offer?: FlightOffer }>;
  createFlightOrder(offerId: string, passengers: PassengerInput[]): Promise<FlightOrderResult>;
  getOrderStatus(orderId: string): Promise<FlightOrderStatus>;
  cancelFlightOrder(orderId: string): Promise<{ success: boolean; refundAmount?: number }>;
}

export interface FlightSearchParams {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  tripType: 'one_way' | 'round_trip' | 'multi_city';
  adults: number;
  children: number;
  infants: number;
  cabinClass: 'economy' | 'premium_economy' | 'business' | 'first';
  directOnly?: boolean;
}

export interface PassengerInput {
  title: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  gender?: string;
  dateOfBirth: string;
  nationality: string;
  passportNumber?: string;
  passportExpiry?: string;
  issuingCountry?: string;
  email: string;
  phone: string;
  frequentFlyerNumber?: string;
}

export interface FlightOrderResult {
  orderId: string;
  pnr?: string;
  ticketNumbers?: string[];
  status: FlightBookingStatus;
  providerReference?: string;
  is_demo: boolean;
}

export type FlightBookingStatus =
  | 'draft' |'price_check_pending' |'price_changed' |'awaiting_payment' |'payment_pending' |'booking_processing' |'confirmed' |'ticketed' |'failed' |'cancelled' |'refunded';

export interface FlightOrderStatus {
  orderId: string;
  status: FlightBookingStatus;
  pnr?: string;
  ticketNumbers?: string[];
  is_demo: boolean;
}

// ─── Demo Provider (no real booking) ─────────────────────────────────────────
export class DemoFlightProvider implements FlightProviderAdapter {
  async searchFlights(params: FlightSearchParams): Promise<FlightOffer[]> {
    // Filter demo offers by origin/destination
    return DEMO_FLIGHT_OFFERS.filter(offer => {
      const seg = offer.segments[0];
      const originMatch = seg.departureAirport.iata === params.origin ||
        seg.departureAirport.city.toLowerCase().includes(params.origin.toLowerCase());
      const destMatch = seg.arrivalAirport.iata === params.destination ||
        seg.arrivalAirport.city.toLowerCase().includes(params.destination.toLowerCase());
      const cabinMatch = params.cabinClass === 'economy' || seg.cabinClass === params.cabinClass;
      const directMatch = !params.directOnly || seg.stops.length === 0;
      return originMatch && destMatch && cabinMatch && directMatch;
    });
  }

  async getFlightOffer(offerId: string): Promise<FlightOffer | null> {
    return DEMO_FLIGHT_OFFERS.find(o => o.id === offerId) || null;
  }

  async revalidateFlightOffer(offerId: string): Promise<{ valid: boolean; newPrice?: number; offer?: FlightOffer }> {
    const offer = DEMO_FLIGHT_OFFERS.find(o => o.id === offerId);
    if (!offer) return { valid: false };
    return { valid: true, offer };
  }

  async createFlightOrder(_offerId: string, _passengers: PassengerInput[]): Promise<FlightOrderResult> {
    // Demo: never issue a real ticket
    return {
      orderId: `DEMO-${Date.now()}`,
      status: 'draft',
      is_demo: true,
    };
  }

  async getOrderStatus(orderId: string): Promise<FlightOrderStatus> {
    return { orderId, status: 'draft', is_demo: true };
  }

  async cancelFlightOrder(_orderId: string): Promise<{ success: boolean; refundAmount?: number }> {
    return { success: true, refundAmount: 0 };
  }
}

export const flightProvider: FlightProviderAdapter = new DemoFlightProvider();
